// src/components/hechos/ui.jsx
//
// Base visual compartida por las pantallas de Hechos Operativos.
//
// Dirección "Registro oficial": el expediente se lee como un registro documental,
// no como un tablero. Serif para los títulos (registro legal), canaleta de horas
// en monoespaciada y jerarquía por reglas y kickers en vez de por tarjetas.
// Membrete institucional arriba, folio como sello y radios cortos.
//
// Disciplina cromática: tinta + un solo acento institucional (el azul de
// CaprepaDesk) + dos estados semánticos (pendiente/concluido). El rojo queda
// reservado para "El hecho": al ser el único, recupera fuerza. La bitácora
// legal se distingue por tipografía y candado, no por color propio.
//
// Los neutros salen de los tokens del tema (--c-*), así que el modo oscuro del
// resto de CaprepaDesk sigue funcionando sin trabajo extra.
import React from 'react';

// Set de iconos propio, en SVG. Nada de emojis como iconografía.
const Ico = ({ children, size = 16, stroke = 1.7 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke} strokeLinecap="round"
         strokeLinejoin="round" style={{ flexShrink: 0 }}>{children}</svg>
);

export const I = {
    shield:  <Ico><path d="M12 3l7 3v6c0 4.2-2.9 7.9-7 9-4.1-1.1-7-4.8-7-9V6l7-3z"/><path d="M9.2 12l2 2 3.6-3.8"/></Ico>,
    check:   <Ico><path d="M5 12.5l4.5 4.5L19 7"/></Ico>,
    clock:   <Ico><circle cx="12" cy="12" r="8"/><path d="M12 7.6V12l2.8 1.8"/></Ico>,
    alert:   <Ico><path d="M12 4.5l8 14H4z"/><path d="M12 10v3.6M12 16.4v.1"/></Ico>,
    lock:    <Ico><rect x="5" y="10.5" width="14" height="9" rx="2"/><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5"/></Ico>,
    doc:     <Ico><path d="M13 3.5H7a1.5 1.5 0 0 0-1.5 1.5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V9z"/><path d="M13 3.5V9h5.5"/></Ico>,
    pdf:     <Ico><path d="M13 3.5H7a1.5 1.5 0 0 0-1.5 1.5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V9z"/><path d="M13 3.5V9h5.5"/><path d="M8.8 16.2c2.6-.9 4.4-3.2 5-5.2M8.8 16.2c1.6-1.3 4.4-1.7 6.4-.7"/></Ico>,
    clip:    <Ico><path d="M16.5 8.5l-6.6 6.6a2.3 2.3 0 0 0 3.3 3.3l7-7a4 4 0 0 0-5.7-5.7l-7 7a5.8 5.8 0 0 0 8.2 8.2l4.3-4.3"/></Ico>,
    pen:     <Ico><path d="M4.5 19.5l1.2-4.2 9.6-9.6a2.05 2.05 0 0 1 2.9 2.9l-9.6 9.6z"/><path d="M13.8 7.2l2.9 2.9"/></Ico>,
    user:    <Ico><circle cx="12" cy="8.5" r="3.6"/><path d="M5 20c.6-3.7 3.4-5.6 7-5.6s6.4 1.9 7 5.6"/></Ico>,
    users:   <Ico><circle cx="9.5" cy="9" r="3.2"/><path d="M3.5 19c.5-3.2 3-4.9 6-4.9s5.5 1.7 6 4.9"/><path d="M16 6.4a3.2 3.2 0 0 1 0 6.2M17.5 14.6c2.1.5 3.4 2.1 3.8 4.4"/></Ico>,
    plus:    <Ico><path d="M12 5v14M5 12h14"/></Ico>,
    send:    <Ico><path d="M20.5 3.5L10.8 13.2M20.5 3.5l-6.2 17-3.5-7.3-7.3-3.5z"/></Ico>,
    pin:     <Ico><path d="M12 21s6.5-5.6 6.5-10.3A6.5 6.5 0 0 0 5.5 10.7C5.5 15.4 12 21 12 21z"/><circle cx="12" cy="10.5" r="2.4"/></Ico>,
    arrow:   <Ico><path d="M5 12h13M13 6.5l5.5 5.5-5.5 5.5"/></Ico>,
    back:    <Ico><path d="M19 12H6M11 17.5 5.5 12 11 6.5"/></Ico>,
    mail:    <Ico><rect x="3" y="5.5" width="18" height="13" rx="2"/><path d="M3.6 7l8.4 6 8.4-6"/></Ico>,
    x:       <Ico><path d="M6.5 6.5l11 11M17.5 6.5l-11 11"/></Ico>,
    download:<Ico><path d="M12 4v11M7.5 10.5 12 15l4.5-4.5"/><path d="M5 19h14"/></Ico>,
    search:  <Ico><circle cx="11" cy="11" r="6.4"/><path d="M15.8 15.8 20 20"/></Ico>,
    chart:   <Ico><path d="M4 4v16h16"/><path d="M8 14l3.5-3.5L15 14l4-5"/></Ico>,
    scale:   <Ico><path d="M12 4v16M7 20h10M4 9l3-4 3 4M4 9a3 3 0 0 0 6 0M14 12l3-4 3 4M14 12a3 3 0 0 0 6 0M7 5l10-1.4"/></Ico>,
};

// Tinta para lo humano (mensajes, bitácora), acento para lo documental, y los
// dos estados. El rojo solo marca el hecho mismo. `ico` es el ícono del nodo
// en la cronología: el tipo se reconoce por forma, no solo por color.
export const KIND = {
    hecho:      { color: 'var(--ho-hecho)',  label: 'El hecho',            ico: 'alert' },
    sistema:    { color: 'var(--c-text-3)',  label: 'Sistema',             ico: 'arrow' },
    ia:         { color: 'var(--ho-accent)', label: 'Relato estructurado', ico: 'pen' },
    validacion: { color: 'var(--ho-ok)',     label: 'Validado',            ico: 'check' },
    documento:  { color: 'var(--ho-accent)', label: 'Documentación',       ico: 'clip' },
    mensaje:    { color: 'var(--ho-ink)',    label: 'Mensaje',             ico: 'mail' },
    bitacora:   { color: 'var(--ho-ink)',    label: 'Bitácora legal',      ico: 'lock' },
    pendiente:  { color: 'var(--ho-pend)',   label: 'Pendiente',           ico: 'clock' },
};

export const fmtFecha = (d, conHora = true) => d
    ? new Date(d).toLocaleString('es-MX', conHora
        ? { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
        : { day: '2-digit', month: 'long', year: 'numeric' })
    : '—';

export const fmtHora = (d) => d
    ? new Date(d).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false })
    : 's/h';

export const fmtMonto = (n) => (n == null || n === '')
    ? 'N/A'
    : new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n);

export const diasDesde = (d) => Math.floor((Date.now() - new Date(d)) / 86400000);

/** Etiqueta de sección en versalitas. Estructura, no decoración. */
export const Kicker = ({ children, color, style }) => (
    <div className="ho-kicker" style={{ color, ...style }}>{children}</div>
);

export const Pill = ({ tone = 'neutro', children, icon }) => (
    <span className={`ho-pill ho-pill-${tone}`}>{icon}{children}</span>
);

/** Campo etiqueta/valor del expediente. */
export const Campo = ({ label, children }) => (
    <div className="ho-campo">
        <div className="ho-campo-k">{label}</div>
        <div className="ho-campo-v">{children ?? 'N/A'}</div>
    </div>
);

/** Membrete institucional. Va arriba de todas las pantallas del módulo. */
export const Membrete = () => (
    <div className="ho-letterhead">
        <span className="brand">{I.shield} Registro de Hechos Operativos</span>
        <span className="tag">Protocolo HO · Uso interno</span>
    </div>
);

const CSS = `
:root {
    --ho-hecho:  #a3312a;
    --ho-accent: #0a4aa8;
    --ho-pend:   #8a5806;
    --ho-ok:     #14653e;
    /* Tinta del despacho: azul marino profundo para títulos, reglas y sello.
       Es la convención de las plataformas legales (Clio, Filevine): navy como
       color de autoridad, no negro pleno. En oscuro cae a la tinta del tema. */
    --ho-ink:    #1c2b45;
    /* IBM Plex Sans en todo el módulo — decisión del negocio (jul 2026): una
       sola voz tipográfica, corporativa y sin cara de plantilla. --ho-serif
       conserva el nombre pero apunta a la misma Plex Sans: es el rol de
       "display/documental" (títulos, cifras, relato), no la familia. */
    --ho-sans:  "IBM Plex Sans", Inter, system-ui, sans-serif;
    --ho-serif: "IBM Plex Sans", Inter, system-ui, sans-serif;
    --ho-mono:  "IBM Plex Mono", "Cascadia Mono", Consolas, ui-monospace, monospace;
    /* Gráficas del mapa de riesgo. El ámbar queda por debajo de 3:1 sobre
       fondo claro, así que nunca carga el dato solo: siempre con etiqueta. */
    --viz-1: var(--ho-accent);
    --viz-alerta: #eda100;
    --viz-grid: var(--c-border);
}
[data-bs-theme="dark"] {
    --ho-hecho:  #e0796e;
    --ho-accent: #7ea0ff;
    --ho-pend:   #d5a044;
    --ho-ok:     #48ae7f;
    --ho-ink:    var(--c-text-1);
    --viz-alerta: #c98500;
}

.ho-wrap { max-width: 1080px; margin: 0 auto; padding: 4px 4px 72px; font-family: var(--ho-sans); }
.ho-wrap .btn, .ho-wrap .form-control, .ho-wrap .form-select { font-family: var(--ho-sans); }
.ho-narrow { max-width: 940px; }

.ho-serif { font-family: var(--ho-serif); font-weight: 400; letter-spacing: -.012em; }
.ho-mono  { font-family: var(--ho-mono); font-variant-numeric: tabular-nums; }

.ho-kicker {
    font-size: 10px; letter-spacing: .15em; text-transform: uppercase;
    font-weight: 700; color: var(--c-text-3);
}
.ho-h1 { font-family: var(--ho-serif); font-weight: 700; font-size: 30px; line-height: 1.16; margin: 6px 0 0; color: var(--ho-ink); }
.ho-h2 { font-family: var(--ho-serif); font-weight: 700; font-size: 20px; margin: 0; color: var(--ho-ink); }

/* Membrete: banda de tinta que encabeza cada pantalla del módulo — como el
   lomo de una carpeta legal. Escudo + nombre a la izquierda, sello de uso a
   la derecha. */
.ho-letterhead {
    display: flex; justify-content: space-between; align-items: center; gap: 12px;
    margin-top: 10px; padding: 9px 14px;
    background: var(--ho-ink); color: var(--c-card);
    border-radius: 4px;
    font-size: 10.5px; letter-spacing: .16em; text-transform: uppercase;
    font-weight: 700;
}
.ho-letterhead .brand { display: inline-flex; align-items: center; gap: 9px; }
.ho-letterhead .tag {
    color: color-mix(in srgb, var(--c-card) 62%, transparent);
    letter-spacing: .14em; font-weight: 600;
}
@media (max-width: 560px) { .ho-letterhead .tag { display: none; } }

/* Folio como sello: recuadro fino, monoespaciada */
.ho-sello {
    border: 1.5px solid var(--ho-ink); border-radius: 2px;
    padding: 6px 14px 7px; text-align: center; min-width: 130px;
}
.ho-sello .k { font-size: 9px; letter-spacing: .22em; text-transform: uppercase; font-weight: 700; color: var(--c-text-3); }
.ho-sello .v { font-family: var(--ho-mono); font-size: 13.5px; letter-spacing: .05em; color: var(--ho-ink); margin-top: 1px; }

/* Encabezado del expediente: regla gruesa, como un documento */
.ho-head { padding: 26px 0 18px; border-bottom: 2px solid var(--ho-ink); }
.ho-head-sub { margin-top: 10px; display: flex; flex-wrap: wrap; gap: 4px 22px; font-size: 13px; color: var(--c-text-2); }
.ho-head-sub b { color: var(--c-text-1); font-weight: 600; }

/* Franja de indicadores: cifras separadas por filetes, como el sumario */
.ho-strip { display: flex; flex-wrap: wrap; border-bottom: 1px solid var(--c-border); }
.ho-strip > div { flex: 1 1 140px; padding: 14px 20px 14px 0; }
.ho-strip > div + div { border-left: 1px solid var(--c-border); padding-left: 20px; }
.ho-strip .k { font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: var(--c-text-3); font-weight: 700; }
.ho-strip .v { font-family: var(--ho-serif); font-size: 21px; margin-top: 2px; color: var(--ho-ink); font-variant-numeric: tabular-nums; }
.ho-strip .v small { font-family: var(--font-sans, inherit); font-size: 12px; color: var(--c-text-3); }

/* Tracker de etapas: monocromo. Cumplidas en tinta, la actual en el acento
   con anillo, futuras vacías. El avance se lee por forma, no por colores. */
.ho-stage { display: flex; padding: 20px 0 6px; }
.ho-stage .s { display: flex; align-items: center; flex: 1; min-width: 0; }
.ho-stage .d { width: 9px; height: 9px; border-radius: 50%; background: var(--c-border); flex: none; }
.ho-stage .s.done .d { background: var(--ho-ink); }
.ho-stage .s.now  .d { background: var(--ho-accent); box-shadow: 0 0 0 4px color-mix(in srgb, var(--ho-accent) 20%, transparent); }
.ho-stage .l { height: 1px; flex: 1; background: var(--c-border); }
.ho-stage .s.done .l { background: var(--ho-ink); }
.ho-stage .s:last-child .l { display: none; }
.ho-stage-l { display: flex; padding-bottom: 18px; border-bottom: 1px solid var(--c-border); }
.ho-stage-l span { flex: 1; font-size: 11px; color: var(--c-text-3); padding-right: 10px; }
.ho-stage-l span.done { color: var(--c-text-2); font-weight: 600; }
.ho-stage-l span.now  { color: var(--ho-accent); font-weight: 700; }

/* Aviso de lo que detiene el expediente */
.ho-focus {
    display: flex; gap: 12px; align-items: flex-start; margin-top: 18px;
    padding: 13px 16px; border-radius: 6px;
    background: color-mix(in srgb, var(--ho-pend) 10%, var(--c-card));
    border: 1px solid color-mix(in srgb, var(--ho-pend) 34%, transparent);
}
.ho-focus .t { font-size: 13px; font-weight: 700; color: var(--ho-pend); }
.ho-focus .d { font-size: 13px; color: var(--c-text-2); margin-top: 2px; }

/* Filtros del hilo */
.ho-filters { display: flex; gap: 7px; flex-wrap: wrap; padding: 20px 0 4px; }
.ho-chip {
    border: 1px solid var(--c-border); background: var(--c-card); color: var(--c-text-2);
    padding: 5px 13px; border-radius: 4px; cursor: pointer; font: inherit;
    font-size: 12.5px; line-height: 1.4;
}
.ho-chip:hover { border-color: var(--c-text-4); }
.ho-chip[aria-pressed="true"] { background: var(--ho-ink); color: var(--c-card); border-color: var(--ho-ink); font-weight: 600; }

/* Separador de día: marca de capítulo, en tinta */
.ho-day {
    display: flex; align-items: center; gap: 14px; margin: 28px 0 16px;
    font-size: 11px; letter-spacing: .16em; text-transform: uppercase;
    color: var(--ho-ink); font-weight: 700;
}
.ho-day::after { content: ""; height: 1px; flex: 1; background: color-mix(in srgb, var(--ho-ink) 28%, transparent); }

/* Entrada del timeline: canaleta de hora + espina + cuerpo. El nodo lleva el
   ícono del tipo — la cronología se lee por forma, no solo por texto. */
.ho-ev { display: grid; grid-template-columns: 60px 36px minmax(0, 1fr); }
.ho-ev .t {
    font-family: var(--ho-mono); font-size: 12px; color: var(--c-text-3);
    text-align: right; padding-top: 8px; font-variant-numeric: tabular-nums;
}
.ho-ev .sp { position: relative; display: flex; justify-content: center; }
.ho-ev .sp::before { content: ""; position: absolute; top: 0; bottom: 0; width: 2px; background: var(--c-border); }
.ho-ev:last-child .sp::before { bottom: auto; height: 16px; }
.ho-ev .node {
    position: relative; margin-top: 2px; width: 27px; height: 27px; border-radius: 50%;
    background: color-mix(in srgb, currentColor 10%, var(--c-bg));
    border: 1.5px solid currentColor; z-index: 1;
    display: flex; align-items: center; justify-content: center;
}
.ho-ev .node svg { width: 13px; height: 13px; }
.ho-ev .bd { padding: 2px 0 26px 12px; min-width: 0; }
.ho-ev .ttl { font-size: 15px; font-weight: 600; margin-top: 3px; color: var(--c-text-1); }

/* Hitos del expediente (folio, etapa, asignación, validación, cierre, el
   hecho): nodo sólido con ícono en blanco y título serif — las estaciones de
   la línea de tiempo se distinguen de la conversación diaria. */
.ho-ev.hito .node { background: currentColor; }
.ho-ev.hito .node svg { stroke: var(--c-card); }
.ho-ev.hito .ttl { font-family: var(--ho-serif); font-size: 17.5px; color: var(--ho-ink); }
.ho-ev .dsc { font-size: 13.5px; color: var(--c-text-2); margin-top: 4px; line-height: 1.62; }
.ho-ev .meta { font-size: 12px; color: var(--c-text-3); margin-top: 7px; display: flex; gap: 16px; flex-wrap: wrap; }

.ho-quote {
    margin-top: 10px; padding: 11px 15px; background: var(--c-msg-bg);
    border-left: 2px solid var(--c-border); font-size: 13.5px; line-height: 1.62;
    color: var(--c-text-2); white-space: pre-wrap;
}
/* La bitácora legal se distingue por la regla de tinta y el candado, no por color */
.ho-ev.legal .ho-quote {
    background: color-mix(in srgb, var(--ho-ink) 5%, var(--c-card));
    border-left-color: var(--ho-ink);
}
/* El relato original: cita enmarcada del acta, en serif. Es la evidencia —
   el cuadro la presenta como documento, sin leyendas alrededor. */
.ho-original {
    margin-top: 18px; padding: 14px 18px;
    background: var(--c-msg-bg);
    border: 1px solid var(--c-border); border-left: 3px solid var(--ho-ink);
    border-radius: 6px;
}
.ho-original .txt {
    font-family: var(--ho-serif); font-size: 14.5px; line-height: 1.78;
    color: var(--c-text-1); white-space: pre-wrap;
}

.ho-privado {
    display: inline-flex; align-items: center; gap: 5px; font-size: 10px;
    letter-spacing: .12em; text-transform: uppercase; font-weight: 700;
    color: var(--c-text-2); margin-left: 10px;
}

.ho-files { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.ho-file {
    display: inline-flex; align-items: center; gap: 7px; padding: 6px 11px;
    background: var(--c-card); border: 1px solid var(--c-border); border-radius: 4px;
    font-size: 12.5px; color: var(--c-text-2); cursor: pointer; text-decoration: none;
}
.ho-file:hover { border-color: var(--c-text-4); color: var(--c-text-1); }

.ho-pend-box {
    padding: 12px 15px; border-radius: 6px;
    background: color-mix(in srgb, var(--ho-pend) 8%, var(--c-card));
    border: 1px solid color-mix(in srgb, var(--ho-pend) 36%, transparent);
    display: flex; gap: 12px; align-items: center; flex-wrap: wrap;
}
.ho-pend-box.hecho { opacity: .55; }
.ho-pend-box .who { margin-left: auto; font-size: 12px; color: var(--c-text-2); }

/* Píldoras */
.ho-pill {
    display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px;
    border-radius: 4px; font-size: 11.5px; font-weight: 600; line-height: 1.5;
    background: var(--c-msg-bg); color: var(--c-text-2);
}
.ho-pill-ok    { background: color-mix(in srgb, var(--ho-ok) 14%, transparent);     color: var(--ho-ok); }
.ho-pill-pend  { background: color-mix(in srgb, var(--ho-pend) 16%, transparent);   color: var(--ho-pend); }
.ho-pill-legal { background: color-mix(in srgb, var(--ho-ink) 8%, transparent);     color: var(--ho-ink); }
.ho-pill-info  { background: color-mix(in srgb, var(--ho-accent) 13%, transparent); color: var(--ho-accent); }
.ho-pill-crit  { background: color-mix(in srgb, var(--ho-hecho) 14%, transparent);  color: var(--ho-hecho); }

/* Campos */
.ho-kv { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 16px 22px; }
.ho-campo-k { font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: var(--c-text-3); font-weight: 700; }
.ho-campo-v { font-size: 14px; margin-top: 2px; color: var(--c-text-1); overflow-wrap: anywhere; }

/* Registro (bandeja) */
.ho-reg { border-top: 2px solid var(--ho-ink); }
.ho-reg-head, .ho-reg-row {
    display: grid; grid-template-columns: 130px minmax(0,1fr) 160px 120px 110px;
    gap: 16px; align-items: center;
}
.ho-reg-head {
    padding: 10px 0; font-size: 10px; letter-spacing: .13em; text-transform: uppercase;
    color: var(--c-text-3); font-weight: 700;
}
.ho-reg-row {
    width: 100%; text-align: left; background: transparent; border: 0;
    border-bottom: 1px solid var(--c-border); padding: 16px 0; cursor: pointer;
    color: inherit; font: inherit;
    transition: background .12s ease, box-shadow .12s ease;
}
.ho-reg-row:hover {
    background: var(--c-ticket-hover-bg);
    box-shadow: inset 3px 0 0 var(--ho-ink);
}
/* Folio como mini-sello: el número de expediente se ve como lo que es */
.ho-folio {
    display: inline-block; font-family: var(--ho-mono); font-size: 11.5px;
    letter-spacing: .04em; color: var(--c-text-2);
    border: 1px solid var(--c-border); border-radius: 2px; padding: 2px 8px;
}
.ho-reg-row:hover .ho-folio { border-color: var(--ho-ink); color: var(--ho-ink); }
.ho-reg-row .ti { font-family: var(--ho-serif); font-size: 16px; color: var(--ho-ink); }
.ho-reg-row .su { font-size: 12px; color: var(--c-text-3); margin-top: 1px; }
.ho-reg-row .am { font-family: var(--ho-mono); font-size: 13px; text-align: right; }
.ho-reg-head .am { text-align: right; }

/* Indicadores de la portada: cifras serif separadas por filetes verticales,
   como el sumario de un informe. */
.ho-sums { display: flex; flex-wrap: wrap; padding: 18px 0 22px; border-bottom: 1px solid var(--c-border); }
.ho-sums > div { padding-right: 36px; }
.ho-sums > div + div { border-left: 1px solid var(--c-border); padding-left: 36px; }
.ho-sums .k { font-size: 10.5px; letter-spacing: .12em; text-transform: uppercase; color: var(--c-text-3); font-weight: 700; }
.ho-sums .v { font-family: var(--ho-serif); font-size: 28px; font-variant-numeric: tabular-nums; color: var(--ho-ink); }
.ho-sums .n { font-size: 12px; color: var(--c-text-3); }

/* Tarjetas de acción del escritorio legal: lo que requiere atención HOY.
   Cada tarjeta es un filtro — clic y la lista muestra exactamente ese corte. */
.ho-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; padding: 18px 0 2px; }
.ho-tile {
    text-align: left; cursor: pointer; font: inherit; color: inherit;
    background: var(--c-card); border: 1px solid var(--c-border); border-radius: 8px;
    padding: 13px 16px 12px;
    transition: border-color .12s ease, box-shadow .12s ease;
}
.ho-tile:hover { border-color: var(--c-text-4); }
.ho-tile[aria-pressed="true"] {
    border-color: var(--ho-accent);
    box-shadow: inset 0 3px 0 var(--ho-accent);
}
.ho-tile .n { font-family: var(--ho-serif); font-weight: 700; font-size: 27px; line-height: 1.05; font-variant-numeric: tabular-nums; color: var(--ho-ink); }
.ho-tile .k { font-size: 10.5px; font-weight: 700; letter-spacing: .11em; text-transform: uppercase; color: var(--c-text-3); margin-top: 4px; }
.ho-tile .d { font-size: 11.5px; color: var(--c-text-3); margin-top: 1px; }
.ho-tile.cero { cursor: default; }
.ho-tile.cero .n { color: var(--c-text-4); }
.ho-tile.cero:hover { border-color: var(--c-border); }

/* Barra de filtros del registro: controles EN CAJA, deliberadamente distintos
   de los campos estilo acta del resto del módulo — esto se opera, no se llena.
   La bandeja de fondo entintada los agrupa como una sola herramienta. */
.ho-filterbar {
    display: flex; gap: 10px; flex-wrap: wrap; align-items: center;
    margin-top: 18px; padding: 12px 14px;
    background: color-mix(in srgb, var(--ho-ink) 3%, var(--c-card));
    border: 1px solid var(--c-border); border-radius: 8px;
}
.ho-search { position: relative; flex: 1 1 260px; max-width: 430px; }
.ho-search .ic {
    position: absolute; left: 11px; top: 50%; transform: translateY(-50%);
    color: var(--ho-accent); pointer-events: none; display: inline-flex;
}
.ho-wrap .ho-search input.form-control {
    width: 100%; background: var(--c-card);
    border: 1.5px solid var(--c-border); border-radius: 6px;
    padding: 9px 12px 9px 36px; font-size: 13.5px;
}
.ho-wrap .ho-search input.form-control:focus {
    border-color: var(--ho-accent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--ho-accent) 15%, transparent);
}
.ho-wrap .ho-filterbar select.form-select {
    background-color: var(--c-card);
    border: 1.5px solid var(--c-border); border-radius: 6px;
    padding: 8px 30px 8px 12px; font-size: 12.5px; font-weight: 600;
    color: var(--c-text-2); width: auto; min-width: 150px;
}
.ho-wrap .ho-filterbar select.form-select:focus {
    border-color: var(--ho-accent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--ho-accent) 15%, transparent);
}
/* Con un valor elegido, el control se pinta del acento: filtro vivo, visible */
.ho-wrap .ho-filterbar select.form-select.activo {
    border-color: var(--ho-accent); color: var(--ho-accent);
}

/* Mini-tracker de etapas por renglón del registro */
.ho-minitrack { display: flex; gap: 4px; margin-top: 6px; }
.ho-minitrack i { width: 6px; height: 6px; border-radius: 50%; background: var(--c-border); }
.ho-minitrack i.done { background: var(--ho-ink); }
.ho-minitrack i.now  { background: var(--ho-accent); box-shadow: 0 0 0 2px color-mix(in srgb, var(--ho-accent) 25%, transparent); }

/* Composer: tarjeta con canto de tinta, como las secciones del alta */
.ho-composer {
    margin-top: 28px; background: var(--c-card);
    border: 1px solid var(--c-border); border-top: 3px solid var(--ho-ink);
    border-radius: 8px; padding: 16px 20px 18px;
}
.ho-composer textarea {
    width: 100%; min-height: 76px; padding: 11px 13px; font: inherit; font-size: 13.5px;
    background: var(--c-input-bg); color: var(--c-text-1);
    border: 1px solid var(--c-border); border-radius: 6px; resize: vertical;
}
.ho-composer.legal textarea { border-color: color-mix(in srgb, var(--ho-ink) 45%, transparent); }

/* Modal de validación: la acción con más peso legal del módulo merece más
   que un window.prompt */
.ho-modal-bk {
    position: fixed; inset: 0; z-index: 1055; padding: 20px;
    background: rgba(10, 14, 22, .55);
    display: flex; align-items: center; justify-content: center;
}
.ho-modal {
    width: 100%; max-width: 680px; padding: 22px 26px 20px;
    background: var(--c-card); border: 1px solid var(--c-border);
    border-top: 3px solid var(--ho-ink); border-radius: 6px;
    box-shadow: 0 28px 70px -18px rgba(0,0,0,.5);
}
.ho-modal textarea {
    width: 100%; min-height: 200px; margin-top: 14px; padding: 11px 13px;
    font: inherit; font-size: 13.5px; line-height: 1.6;
    background: var(--c-input-bg); color: var(--c-text-1);
    border: 1px solid var(--c-border); border-radius: 6px; resize: vertical;
}
.ho-modal .aviso { font-size: 12px; color: var(--c-text-3); margin-top: 8px; }

.ho-empty { padding: 48px 20px; text-align: center; color: var(--c-text-3); }
.ho-empty .t { font-family: var(--ho-serif); font-weight: 700; font-size: 19px; color: var(--ho-ink); margin: 10px 0 4px; }

/* Skeleton de carga: la pantalla llega dibujada, no con un letrero */
.ho-skel {
    position: relative; overflow: hidden; border-radius: 4px;
    background: color-mix(in srgb, var(--c-text-1) 7%, transparent);
}
.ho-skel::after {
    content: ""; position: absolute; inset: 0; transform: translateX(-100%);
    background: linear-gradient(90deg, transparent,
        color-mix(in srgb, var(--c-card) 65%, transparent), transparent);
    animation: ho-shimmer 1.4s infinite;
}
@keyframes ho-shimmer { 100% { transform: translateX(100%); } }

/* ── Formularios del módulo: estilo acta, no caja Bootstrap ─────────────
   Los campos de una línea se llenan sobre un subrayado, como un formato
   oficial; el foco lo marca el acento. Solo dentro de .ho-wrap: el resto
   de CaprepaDesk conserva sus controles de siempre. */
.ho-wrap .form-control, .ho-wrap .form-select {
    background: transparent;
    border: 0; border-bottom: 1.5px solid var(--c-border);
    border-radius: 0; padding: 6px 2px;
    color: var(--c-text-1); box-shadow: none;
    transition: border-color .15s ease, box-shadow .15s ease;
}
.ho-wrap .form-control:focus, .ho-wrap .form-select:focus {
    background: transparent;
    border-bottom-color: var(--ho-accent);
    box-shadow: 0 1px 0 0 var(--ho-accent);
}
.ho-wrap .form-control::placeholder { color: var(--c-text-4); }
.ho-wrap .form-select { padding-right: 30px; }

/* El relato y los campos largos sí llevan caja: ahí se escribe de corrido.
   En serif, porque lo que se captura es la narración del acta. */
.ho-wrap textarea.form-control {
    font-family: var(--ho-serif); font-size: 14.5px; line-height: 1.7;
    background: var(--c-input-bg);
    border: 1px solid var(--c-border); border-radius: 6px; padding: 11px 13px;
}
.ho-wrap textarea.form-control:focus {
    border-color: var(--ho-accent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--ho-accent) 14%, transparent);
}

/* Botones del módulo: rectos, como el resto de la identidad */
.ho-wrap .btn { border-radius: 4px; }

/* Agrupadores del formulario: cada bloque de preguntas es un recuadro con
   canto de tinta — la página se lee por secciones enmarcadas, no como una
   sábana de campos. */
.ho-fs {
    background: var(--c-card);
    border: 1px solid var(--c-border);
    border-top: 3px solid var(--ho-ink);
    border-radius: 8px;
    padding: 18px 20px 16px;
    margin-top: 18px;
}
.ho-fs.first { margin-top: 22px; }
.ho-fs-title { font-family: var(--ho-serif); font-weight: 700; font-size: 17px; color: var(--ho-ink); }
.ho-fs-sub { font-size: 12.5px; color: var(--c-text-3); margin: 2px 0 14px; line-height: 1.5; }
.ho-fs-title + .row { margin-top: 2px; }

/* Pregunta del formulario: en lenguaje llano, con su ayuda debajo */
.ho-lbl { display: block; font-size: 13px; font-weight: 600; color: var(--c-text-1); margin-bottom: 1px; }
.ho-lbl .req { color: var(--ho-hecho); }
.ho-lbl small { display: block; font-weight: 400; font-size: 11.5px; color: var(--c-text-3); margin: 1px 0 3px; line-height: 1.45; }

/* Tarjeta de opción (p. ej. "¿A quién le ocurrió?"): al elegirla queda
   sellada con la barra de tinta al margen. */
.ho-opt {
    width: 100%; text-align: left; padding: 16px 18px; border-radius: 6px;
    background: var(--c-card); color: var(--c-text-1);
    border: 1.5px solid var(--c-border); cursor: pointer; font: inherit;
    transition: border-color .15s ease, box-shadow .15s ease;
}
.ho-opt:hover { border-color: var(--c-text-4); }
.ho-opt[aria-pressed="true"] {
    border-color: var(--ho-ink);
    box-shadow: inset 3px 0 0 var(--ho-ink);
    background: color-mix(in srgb, var(--ho-ink) 4%, var(--c-card));
}

/* Tarjetas y tablas de analítica/administración */
.viz-card {
    background: var(--c-card); border: 1px solid var(--c-border);
    border-radius: 8px; padding: 18px 20px 10px;
}
.viz-card + .viz-card { margin-top: 16px; }
.viz-title { font-size: 14px; font-weight: 600; color: var(--c-text-1); }
.viz-sub { font-size: 12.5px; color: var(--c-text-3); margin-top: 2px; line-height: 1.5; }
.viz-tip {
    background: var(--c-card); border: 1px solid var(--c-border); border-radius: 6px;
    padding: 8px 11px; font-size: 12.5px; color: var(--c-text-1);
    box-shadow: 0 6px 20px -8px rgba(0,0,0,.3);
}
.viz-tip .k { color: var(--c-text-3); }

.viz-tabla { width: 100%; border-collapse: collapse; font-size: 13px; }
.viz-tabla th {
    text-align: left; font-size: 10px; letter-spacing: .11em; text-transform: uppercase;
    color: var(--c-text-3); font-weight: 700; padding: 8px 12px 8px 0;
    border-bottom: 1px solid var(--c-border); white-space: nowrap;
}
.viz-tabla td { padding: 10px 12px 10px 0; border-bottom: 1px solid var(--c-border); }
.viz-tabla tr:last-child td { border-bottom: 0; }
.viz-tabla .num { text-align: right; font-variant-numeric: tabular-nums; font-family: var(--ho-mono); }
.viz-barra { height: 6px; border-radius: 3px; background: var(--viz-1); min-width: 3px; }

@media (max-width: 820px) {
    .ho-h1 { font-size: 23px; }
    .ho-sums > div + div { border-left: 0; padding-left: 0; }
    .ho-strip > div + div { border-left: 0; padding-left: 0; }
    .ho-kv { grid-template-columns: 1fr; }
    .ho-ev { grid-template-columns: 46px 32px minmax(0,1fr); }
    .ho-stage-l span { font-size: 9px; }
    .ho-reg-head, .ho-reg-row { grid-template-columns: minmax(0,1fr) 96px; }
    .ho-reg-head span:nth-child(1), .ho-reg-row span:nth-child(1),
    .ho-reg-head span:nth-child(3), .ho-reg-row span:nth-child(3),
    .ho-reg-head span:nth-child(4), .ho-reg-row span:nth-child(4) { display: none; }
}
`;

/** Inyecta el CSS del módulo una sola vez. */
export function HechosStyles() {
    return <style>{CSS}</style>;
}

/** Tracker de etapas con conector, compartido por registro y expediente. */
export function Tracker({ etapas, actual }) {
    const idx = etapas.findIndex(e => e.key === actual);
    return (
        <>
            <div className="ho-stage">
                {etapas.map((e, i) => (
                    <span key={e.key} className={`s ${i < idx ? 'done' : ''} ${i === idx ? 'now' : ''}`}>
                        <span className="d" />
                        <span className="l" />
                    </span>
                ))}
            </div>
            <div className="ho-stage-l">
                {etapas.map((e, i) => (
                    <span key={e.key} className={i < idx ? 'done' : i === idx ? 'now' : ''}>
                        {e.label}
                    </span>
                ))}
            </div>
        </>
    );
}
